// Which records can go in an export batch — the frontend's copy of the
// backend collectors' rules (app/batchexports/crud.py), read from the SAME
// tenant config rows so the two cannot disagree about a tenant (H281).
import type { ExportEligibility } from "./CreateExportBatchAction";

// invoices_config.inc:112 `invoices_export_status`: legacy's default and every
// fleet tenant's value (measured 2026-09-30). crud.py DEFAULT_INVOICE_EXPORT_STATUSES.
const DEFAULT_INVOICE_EXPORT_STATUSES = [2, 4];

/** A config value as its codes: ["2", "4"] (the /v6/config list form), "4" or "2,4". */
function codes(value: unknown): number[] {
  const parts = Array.isArray(value) ? value : String(value ?? "").split(",");
  return parts.map((p) => String(p).trim()).filter((p) => /^\d+$/.test(p)).map(Number);
}

/** A record is unexported while its back-link is empty: legacy's ifnull(exported, 0) = 0. */
export const isUnexported = (value: unknown) => !Number(value);

/**
 * Invoices: in the tenant's `invoices_export_status`, not yet exported (a
 * batch id, or v6's Xero push's 1), and not readonly (legacy's edit lock; its
 * picker offered no checkbox on one, invoices_display.inc:214-215). The codes are the tenant's — g2 labels
 * 2 Invoiced / 4 Complete, legacy's default 2 Bill Out / 4 Invoiced — so the
 * reason names them with the tenant's own `invoices_status` labels.
 * Null until the config has loaded: the entry then runs and the backend decides.
 */
export function invoiceExportEligibility(invoiceConfig: any): ExportEligibility | null {
  if (!invoiceConfig) return null;
  const configured = codes(invoiceConfig.export_status);
  const allowed = configured.length > 0 ? configured : DEFAULT_INVOICE_EXPORT_STATUSES;
  const labels = invoiceConfig.status ?? {};
  const names = allowed.map((code) => labels[code] ?? `status ${code}`).join(" or ");
  return {
    isEligible: (row: any) =>
      allowed.includes(Number(row?.status_id)) && isUnexported(row?.exported) && !row?.readonly,
    reason: `Only ${names} invoices that are not exported or read-only can go in a batch`,
  };
}
